import { Action, Module, VuexModule, Mutation, getModule } from 'vuex-module-decorators';
import moment from 'moment';
import store from '@/store';
import { ApiConfig } from '@/types/observe';
import { Fully } from '@/types/fully';
declare let fully: Fully;

export enum ScreenMode {
  None = 'none',
  Default = 'default',
  Train = 'train',
  Twitter = 'twitter'
}

export interface ConfigStatus {
  showFixedmode: boolean;
  fixedmodeUri: string;
  routename: string;
}

@Module({ dynamic: true, store, name: 'config', namespaced: true, preserveState: false })
class ConfigModlue extends VuexModule implements ConfigStatus {
  /**
   * 画面モード設定
   *
   * @type {ScreenMode}
   * @memberof ConfigModlue
   */
  private screenmode: ScreenMode = ScreenMode.None;

  /**
   * 画面モードのルート名取得
   *
   * @readonly
   * @type {string}
   * @memberof ConfigModlue
   */
  public get routename(): string {
    const routes = {
      none: 'home',
      default: 'split',
      train: 'split.train',
      twitter: 'split.twitter'
    };
    return routes[this.screenmode];
  }

  /**
   * TwitterID
   *
   * @type {string}
   * @memberof ConfigModlue
   */
  public twitterid = '';

  /**
   * 固定表示モード設定
   *
   * @type {boolean}
   * @memberof ConfigModlue
   */
  public showFixedmode = false;
  public fixedmodeUri = '';

  // スリープ時間設定
  private sleepFromTime: string | null = null;
  private sleepToTime: string | null = null;
  private sleepFromTimeAt: moment.Moment | null = null;
  private sleepToTimeAt: moment.Moment | null = null;
  private isSleeping = false;
  private isSleepFromAfter = false;

  // 更新時間設定
  private reloadTime: string | null = null;
  private reloadTimeAt: moment.Moment | null = null;
  private isReloaded = false;

  /**
   * 設定情報を読み込む
   *
   * @public
   * @param {ApiConfig} config
   * @memberof ConfigModule
   */
  @Mutation
  public setDatas(config: ApiConfig) {
    this.screenmode = (config.screenmode as ScreenMode) || ScreenMode.None;
    this.twitterid = config.twitterid || '';
    this.fixedmodeUri = config.fixedmodeUri || '';
    if (this.fixedmodeUri !== '') {
      this.showFixedmode = config.fixedmode || false;
    } else {
      this.showFixedmode = false;
    }
    const sleepActive = config.sleepActive || false;
    this.sleepFromTime = sleepActive ? config.sleepFromTime || null : null;
    this.sleepToTime = sleepActive ? config.sleepToTime || null : null;
    const reloadActive = config.reloadActive || false;
    this.reloadTime = reloadActive ? config.reloadTime || null : null;

    // スリープ時間の初期設定
    if (this.sleepFromTime !== null && this.sleepToTime !== null) {
      const now = moment();
      this.sleepFromTimeAt = moment(now.format('YYYY-MM-DD') + ' ' + this.sleepFromTime);
      this.sleepToTimeAt = moment(now.format('YYYY-MM-DD') + ' ' + this.sleepToTime);
      this.isSleepFromAfter = this.sleepFromTimeAt.isSameOrAfter(this.sleepToTimeAt);

      // スリープ開始日時 >= スリープ終了日時 だったら、スリープ開始日時を次の日にする
      if (this.isSleepFromAfter) {
        this.sleepToTimeAt = moment(now.add(1, 'd').format('YYYY-MM-DD') + ' ' + this.sleepToTime);
      } else {
        // スリープ終了日時が現在日時を過ぎていたら、スリープ開始日時を次の日にする
        if (this.sleepToTimeAt.isSameOrBefore(now)) {
          this.sleepFromTimeAt = moment(now.add(1, 'd').format('YYYY-MM-DD') + ' ' + this.sleepFromTime);
        }
      }
    }

    // リモート更新時間の初期設定
    if (this.reloadTime !== null) {
      const now = moment();
      this.reloadTimeAt = moment(now.format('YYYY-MM-DD') + ' ' + this.reloadTime);

      // 最初に、リモート更新時間がすでに過ぎている場合はリロード済みフラグをTrueにする
      this.isReloaded = this.reloadTimeAt.isSameOrBefore(now);
    }
  }

  /**
   * スリープ状態にする
   *
   * @private
   * @param {boolean} value
   * @memberof ConfigModlue
   */
  @Mutation
  private setSleep(value: boolean) {
    this.isSleeping = value;

    // スリープ日時の更新
    const now = moment();
    if (value) {
      if (this.sleepToTime !== null) {
        // スリープ状態になったら、次のスリープ終了時間を更新する
        if (this.isSleepFromAfter) {
          this.sleepToTimeAt = moment(now.add(1, 'd').format('YYYY-MM-DD') + ' ' + this.sleepToTime);
        } else {
          this.sleepToTimeAt = moment(now.format('YYYY-MM-DD') + ' ' + this.sleepToTime);
        }
      }
    } else {
      if (this.sleepFromTime !== null) {
        // スリープ解除になったら、次のスリープ開始時間を更新する
        if (this.isSleepFromAfter) {
          this.sleepFromTimeAt = moment(now.format('YYYY-MM-DD') + ' ' + this.sleepFromTime);
        } else {
          this.sleepFromTimeAt = moment(now.add(1, 'd').format('YYYY-MM-DD') + ' ' + this.sleepFromTime);
        }
      }
    }

    try {
      if (value) {
        fully.turnScreenOff();
      } else {
        fully.turnScreenOn();
      }
    } catch (e) {
      //
    }
    if (process.env.NODE_ENV === 'development') {
      if (value) {
        console.log('fully.turnScreenOff()');
      } else {
        console.log('fully.turnScreenOn()');
      }
    }
  }

  /**
   * リモート更新日時を設定
   *
   * @private
   * @param {string} time
   * @memberof ConfigModlue
   */
  @Mutation
  private setReloadTimeAt(time: string) {
    this.reloadTimeAt = moment(moment().format('YYYY-MM-DD') + ' ' + time);
  }

  /**
   * リロード済みフラグを設定
   *
   * @private
   * @param {boolean} value
   * @memberof ConfigModlue
   */
  @Mutation
  private setIsReloaded(value: boolean) {
    this.isReloaded = value;
  }

  /**
   * スリープ時間の監視
   *
   * @memberof ConfigModlue
   */
  @Action({})
  public tickSleep() {
    if (this.sleepFromTimeAt !== null && this.sleepToTimeAt !== null) {
      const now = moment();
      if (this.isSleeping) {
        // スリープ状態で尚且つスリープ終了時間を過ぎていたら、スリープ状態を解除する
        const isSleep = this.sleepToTimeAt.isAfter(now);
        if (!isSleep) {
          this.setSleep(false);
        }
      } else {
        // 非スリープ状態で尚且つスリープ開始時間を過ぎたら、スリープ状態にする
        const isSleep = this.sleepFromTimeAt.isSameOrBefore(now);
        if (isSleep) {
          this.setSleep(true);
        }
      }
    }
  }

  /**
   * コンテンツリストのリモート更新のチェック
   *
   * @returns {Promise<void>}
   * @memberof ConfigModlue
   */
  @Action({})
  public async checkReload(): Promise<void> {
    return new Promise(resolve => {
      if (this.reloadTime !== null && this.reloadTimeAt !== null) {
        const now = moment();

        // 日付が変わっていたらリロード時間を更新
        if (!this.reloadTimeAt.isSame(now, 'day')) {
          this.setReloadTimeAt(this.reloadTime);
          this.setIsReloaded(false);
        }

        if (!this.isReloaded) {
          // すでにリロードされていたら処理をしない
          const isReload = this.reloadTimeAt.isSameOrBefore(now);
          if (isReload) {
            this.setIsReloaded(true);
            resolve();
          }
        }
      }
    });
  }
}

export default getModule(ConfigModlue, store);
